import { useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTeacherAvailabilityRules } from '@/hooks/useTeacherAvailabilityRules';
import { useAvailabilityGroups } from '@/hooks/useAvailabilityGroups';
import { availabilityApi, type AvailabilityEffect } from '@/lib/availability';

type Props = {
  wpUserId: number;
  standorte: Array<{ id: number; name: string }>;
  personStandorteIds: number[];
  defaultStandortId: number | null;
};

export default function TeacherAvailabilityRulesSection({
  wpUserId,
  standorte,
  personStandorteIds,
  defaultStandortId,
}: Readonly<Props>) {
  const { groups } = useAvailabilityGroups();
  const { groupRules, overrides, loading, reload } = useTeacherAvailabilityRules(wpUserId, true);

  const [newGroupId, setNewGroupId] = useState('');
  const [newGroupEffect, setNewGroupEffect] = useState<AvailabilityEffect>('deny');
  const [showOverrideForm, setShowOverrideForm] = useState(false);
  const [ovFrom, setOvFrom] = useState('');
  const [ovTo, setOvTo] = useState('');
  const [ovEffect, setOvEffect] = useState<AvailabilityEffect>('allow');
  const [ovTitle, setOvTitle] = useState('');
  const [ovStandortId, setOvStandortId] = useState(defaultStandortId ? String(defaultStandortId) : '__all__');

  const assignedStandorte = standorte.filter(s => personStandorteIds.includes(s.id));

  const handleAddGroupRule = async () => {
    if (!newGroupId) {
      toast.error('Gruppe auswählen');
      return;
    }
    try {
      await availabilityApi.createTeacherGroupRule(wpUserId, {
        groupId: Number(newGroupId),
        effect: newGroupEffect,
        priority: 150,
      });
      toast.success('Gruppenregel gespeichert');
      setNewGroupId('');
      await reload();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Fehler');
    }
  };

  const handleAddOverride = async () => {
    if (!ovFrom || !ovTo) {
      toast.error('Von- und Bis-Datum erforderlich');
      return;
    }
    try {
      await availabilityApi.createTeacherOverride(wpUserId, {
        dateFrom: ovFrom,
        dateTo: ovTo,
        effect: ovEffect,
        title: ovTitle || null,
        standortId: ovStandortId && ovStandortId !== '__all__' ? Number(ovStandortId) : null,
        priority: 250,
        aktiv: true,
      });
      toast.success('Ausnahme gespeichert');
      setShowOverrideForm(false);
      setOvFrom('');
      setOvTo('');
      setOvTitle('');
      await reload();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Fehler');
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Ferien- & Feiertagsgruppen</CardTitle>
          <CardDescription>
            z. B. „Schulferien Niedersachsen“ als nicht verfügbar. Individuelle Ausnahmen unten haben Vorrang.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-2 md:flex-row md:items-end">
                <div className="flex-1 space-y-1">
                  <Label>Gruppe</Label>
                  <Select value={newGroupId} onValueChange={setNewGroupId}>
                    <SelectTrigger><SelectValue placeholder="Gruppe wählen" /></SelectTrigger>
                    <SelectContent>
                      {groups.map(g => (
                        <SelectItem key={g.id} value={String(g.id)}>{g.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-full md:w-48 space-y-1">
                  <Label>Wirkung</Label>
                  <Select value={newGroupEffect} onValueChange={v => setNewGroupEffect(v as AvailabilityEffect)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="deny">Nicht verfügbar</SelectItem>
                      <SelectItem value="allow">Verfügbar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="button" onClick={handleAddGroupRule}>Hinzufügen</Button>
              </div>
              <div className="divide-y rounded border">
                {groupRules.map(rule => (
                  <div key={rule.id} className="flex items-center justify-between p-3">
                    <div>
                      <div className="font-medium">{rule.groupName}</div>
                      <div className="text-xs text-muted-foreground">
                        {rule.effect === 'deny' ? 'Nicht verfügbar' : 'Verfügbar'}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive"
                      onClick={async () => {
                        await availabilityApi.deleteTeacherGroupRule(wpUserId, rule.id);
                        await reload();
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {groupRules.length === 0 && (
                  <div className="p-3 text-sm text-muted-foreground">Keine Gruppen zugeordnet</div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Individuelle Zeiträume</CardTitle>
            <CardDescription>
              z. B. erste Sommerferienwoche als verfügbar, obwohl die Gruppe „Schulferien“ sonst sperrt.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowOverrideForm(true)}>
            <Plus className="mr-2 h-4 w-4" />Ausnahme
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {showOverrideForm && (
            <div className="grid gap-3 rounded-lg border p-4 md:grid-cols-2">
              <div>
                <Label>Titel</Label>
                <Input value={ovTitle} onChange={e => setOvTitle(e.target.value)} placeholder="Sommerferien Woche 1" />
              </div>
              <div>
                <Label>Standort (optional)</Label>
                <Select value={ovStandortId} onValueChange={setOvStandortId}>
                  <SelectTrigger><SelectValue placeholder="Alle Standorte" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Alle</SelectItem>
                    {assignedStandorte.map(s => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Von</Label>
                <Input type="date" value={ovFrom} onChange={e => setOvFrom(e.target.value)} />
              </div>
              <div>
                <Label>Bis</Label>
                <Input type="date" value={ovTo} onChange={e => setOvTo(e.target.value)} />
              </div>
              <div>
                <Label>Wirkung</Label>
                <Select value={ovEffect} onValueChange={v => setOvEffect(v as AvailabilityEffect)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="allow">Verfügbar</SelectItem>
                    <SelectItem value="deny">Nicht verfügbar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2 flex gap-2">
                <Button onClick={handleAddOverride}>Speichern</Button>
                <Button variant="outline" onClick={() => setShowOverrideForm(false)}>Abbrechen</Button>
              </div>
            </div>
          )}
          <div className="divide-y rounded border">
            {overrides.map(ov => (
              <div key={ov.id} className="flex items-center justify-between p-3">
                <div>
                  <div className="font-medium">{ov.title || `${ov.dateFrom} – ${ov.dateTo}`}</div>
                  <div className="text-xs text-muted-foreground">
                    {ov.effect === 'allow' ? 'Verfügbar' : 'Nicht verfügbar'}
                    {ov.standortId ? ` · Standort ${ov.standortId}` : ''}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive"
                  onClick={async () => {
                    await availabilityApi.deleteTeacherOverride(wpUserId, ov.id);
                    await reload();
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {overrides.length === 0 && (
              <div className="p-3 text-sm text-muted-foreground">Keine individuellen Ausnahmen</div>
            )}
          </div>
        </CardContent>
      </Card>
    </>
  );
}
